/**
 * Louisiana State-Specific Diagnostic Questions
 * CORRECTED: All citations reference LAC 48:I.Chapter 91 (Home Health Agencies)
 * Regulatory Body: Louisiana Department of Health (LDH) - Health Standards Section (HSS)
 * Last Updated: December 2025
 * 
 * Key Louisiana-Specific Elements:
 * - LAC 48:I.Chapter 91 (not Chapter 89)
 * - R.S. 40:1203.1-5 criminal background check requirements
 * - DSW/CNA Registry checks every 6 months
 * - Dual abuse reporting: APS (18-59) and EPS (60+)
 * - 6-year record retention (not 5 years)
 * - Louisiana Model Emergency Preparedness Plan (hurricane state)
 * - $300,000 GL / $100,000 PL insurance minimums
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const LOUISIANA_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'LA-A1',
    itemId: 'A1',
    stateCode: 'LA',
    title: 'Home Health Agency License Status',
    description: 'Louisiana requires all HHAs to be licensed by LDH under LAC 48:I.Chapter 91',
    severity: 'high',
    regulationCode: '48-I-9105',
    regulationName: 'Licensure',
    agencyName: 'Louisiana Department of Health - Health Standards Section',
    agencyAbbr: 'LDH-HSS',
    agencyPhone: '(225) 342-0138',
    agencyUrl: 'https://ldh.la.gov/health-standards-section/home-health',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Agency license from LDH?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Full HHA licenses are valid for one year. Check status with LDH Health Standards Section.',
      },
      {
        id: 'q2',
        question: 'What is your HHA license expiration date?',
        inputType: 'date',
        helpText: 'Found on your LDH license certificate',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Is your license displayed in a conspicuous place in your agency?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required by LAC 48:I.9105.D',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 30, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your LDH Home Health Agency license is current per LAC 48:I.9105.',
      atRisk: 'Your HHA license expires within 30 days. Submit renewal application to LDH-HSS immediately. Renewal must be submitted at least 30 days before expiration.',
      nonCompliant: 'Operating without a valid LDH HHA license violates LAC 48:I.9105 and R.S. 40:2116.31 et seq. Contact LDH-HSS at (225) 342-0138 immediately.',
      unknown: 'Verify your HHA license status with LDH-HSS at (225) 342-0138.',
    },
    stateNotes: [
      'Full HHA licenses are valid for one year per LAC 48:I.9105',
      'Renewal must be submitted at least 30 days before expiration',
      'License must be displayed conspicuously per LAC 48:I.9105.D',
      'LDH-HSS conducts periodic surveys and complaint investigations',
      'Operating without license is a criminal violation under R.S. 40:2116.31',
    ],
  },

  {
    id: 'LA-A2',
    itemId: 'A2',
    stateCode: 'LA',
    title: 'Administrator Qualifications',
    description: 'Louisiana requires a qualified Administrator per LAC 48:I.9103.A',
    severity: 'high',
    regulationCode: '48-I-9103.A',
    regulationName: 'Administrator',
    agencyName: 'Louisiana Department of Health - Health Standards Section',
    agencyAbbr: 'LDH-HSS',
    agencyPhone: '(225) 342-0138',
    agencyUrl: 'https://ldh.la.gov/health-standards-section/home-health',
    questions: [
      {
        id: 'q1',
        question: 'Does your Administrator meet ONE of the following Louisiana qualifications? (Select which applies)',
        inputType: 'select',
        options: [
          { value: 'physician', label: 'Licensed physician' },
          { value: 'rn', label: 'Registered Nurse (RN)' },
          { value: 'bachelor_post2018', label: 'Bachelor\'s degree (employed as admin after 1/13/2018)' },
          { value: 'exp_pre2018', label: '6+ years healthcare experience (employed as admin before 1/13/2018)' },
          { value: 'health_admin', label: 'Health service administration experience + 1 year supervisory experience' },
          { value: 'none', label: 'None of the above' },
        ],
        required: true,
        helpText: 'Louisiana LAC 48:I.9103.A.1 requires 3 years healthcare management experience PLUS one of these credentials',
      },
      {
        id: 'q2',
        question: 'Does your Administrator have at least 3 years of documented management experience in healthcare delivery?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, documented' },
          { value: 'no', label: 'No / not documented' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Is your Administrator designated in writing?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q4',
        question: 'Is there a qualified written alternate designated when Administrator is unavailable?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required if Administrator manages more than one agency per LAC 48:I.9103.A.1',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'none', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q4', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'notEquals', questionId: 'q1', value: 'none' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q3', value: 'no' },
        { condition: 'equals', questionId: 'q4', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Administrator qualifications meet LAC 48:I.9103.A requirements.',
      atRisk: 'Administrator designation or alternate documentation is incomplete. Ensure written designation and qualified alternate are documented.',
      nonCompliant: 'Administrator does not meet LAC 48:I.9103.A qualification requirements. Louisiana requires 3 years healthcare management experience plus specific credentials.',
      unknown: 'Review Administrator qualifications against LAC 48:I.9103.A.',
    },
    stateNotes: [
      'LAC 48:I.9103.A.1 requires 3 years healthcare management experience',
      'Credential options: MD, RN, bachelor\'s (post-2018), or 6+ years experience (pre-2018)',
      'Written alternate designation required when Administrator is unavailable',
      'Administrator may manage more than one agency with qualified alternates',
      '2 hours annual CE required per LAC 48:I.9103.A.3',
    ],
  },

  {
    id: 'LA-A3',
    itemId: 'A3',
    stateCode: 'LA',
    title: 'Clinical Manager Requirements',
    description: 'Louisiana requires a separate qualified Clinical Manager per LAC 48:I.9103.B',
    severity: 'high',
    regulationCode: '48-I-9103.B',
    regulationName: 'Clinical Manager',
    agencyName: 'Louisiana Department of Health - Health Standards Section',
    agencyAbbr: 'LDH-HSS',
    agencyPhone: '(225) 342-0138',
    agencyUrl: 'https://ldh.la.gov/health-standards-section/home-health',
    questions: [
      {
        id: 'q1',
        question: 'Is your Clinical Manager a Registered Nurse currently licensed in Louisiana?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your Clinical Manager have at least 3 years RN experience with 1 year full-time in home health?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Is your Clinical Manager a full-time employee of ONLY your HHA (not working full-time at any other licensed healthcare agency)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, full-time employee of only this HHA' },
          { value: 'no', label: 'No / works at other facilities' },
        ],
        required: true,
        helpText: 'LAC 48:I.9103.B.1 prohibits Clinical Manager from working full-time at another licensed healthcare agency',
      },
      {
        id: 'q4',
        question: 'Is there a written designation for an RN alternate Clinical Manager during absences?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q4', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Clinical Manager meets all LAC 48:I.9103.B requirements.',
      atRisk: 'Alternate Clinical Manager is not designated in writing. Document an RN alternate.',
      nonCompliant: 'Clinical Manager does not meet LAC 48:I.9103.B requirements. Must be a Louisiana-licensed RN with 3 years experience (1 year in home health), working full-time at only this HHA.',
      unknown: 'Review Clinical Manager qualifications against LAC 48:I.9103.B.',
    },
    stateNotes: [
      'Must be Louisiana-licensed RN with 3 years experience per LAC 48:I.9103.B',
      'At least 1 year full-time home health experience required',
      'Cannot work full-time at another licensed healthcare agency',
      'Written RN alternate designation required during absences',
      '2 hours annual CE required per LAC 48:I.9103.B.3',
    ],
  },

  {
    id: 'LA-A4',
    itemId: 'A4',
    stateCode: 'LA',
    title: 'Insurance Requirements',
    description: 'Louisiana requires specific insurance coverage per LAC 48:I.9105.A.1.d',
    severity: 'high',
    regulationCode: '48-I-9105.A.1.d',
    regulationName: 'Insurance',
    agencyName: 'Louisiana Department of Health - Health Standards Section',
    agencyAbbr: 'LDH-HSS',
    agencyPhone: '(225) 342-0138',
    agencyUrl: 'https://ldh.la.gov/health-standards-section/home-health',
    questions: [
      {
        id: 'q1',
        question: 'What is your General Liability insurance coverage per occurrence?',
        inputType: 'select',
        options: [
          { value: '300k_plus', label: '$300,000 or more per occurrence' },
          { value: 'below_300k', label: 'Below $300,000 per occurrence' },
          { value: 'none', label: 'No general liability insurance' },
        ],
        required: true,
        helpText: 'Louisiana requires minimum $300,000 per occurrence',
      },
      {
        id: 'q2',
        question: 'Do you have Workers\' Compensation insurance as required by state law?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'What Professional Liability coverage do you have?',
        inputType: 'select',
        options: [
          { value: 'standard', label: '$100,000 per occurrence / $300,000 aggregate' },
          { value: 'self_pcf', label: '$100,000 self-insurance + enrolled in Louisiana Patient Compensation Fund' },
          { value: 'self_no_pcf', label: '$1M per occurrence / $3M aggregate (self-insured without PCF)' },
          { value: 'below_min', label: 'Below minimum requirements' },
          { value: 'none', label: 'No professional liability coverage' },
        ],
        required: true,
        helpText: 'Louisiana allows self-insurance with PCF enrollment, or higher limits without PCF',
      },
      {
        id: 'q4',
        question: 'Is LDH-HSS listed as certificate holder on your insurance policies?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per LAC 48:I.9105.A.1.d note',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: '300k_plus' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'includes', questionId: 'q3', value: ['standard', 'self_pcf', 'self_no_pcf'] },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q4', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'none' },
        { condition: 'equals', questionId: 'q3', value: 'none' },
      ], result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'below_300k' },
        { condition: 'equals', questionId: 'q3', value: 'below_min' },
        { condition: 'equals', questionId: 'q4', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Insurance coverage meets LAC 48:I.9105.A.1.d requirements.',
      atRisk: 'Insurance coverage may not meet Louisiana minimums or LDH-HSS is not listed as certificate holder. Review your policies.',
      nonCompliant: 'Insurance coverage does not meet LAC 48:I.9105.A.1.d requirements. Louisiana requires: GL $300,000/occurrence, Workers Comp, and Professional Liability $100,000/$300,000.',
      unknown: 'Verify insurance coverage meets LAC 48:I.9105.A.1.d requirements.',
    },
    stateNotes: [
      'General Liability: minimum $300,000 per occurrence per LAC 48:I.9105.A.1.d',
      'Professional Liability: $100,000/$300,000 or self-insure with Louisiana PCF enrollment',
      'Workers Compensation insurance required by Louisiana law',
      'LDH-HSS must be listed as certificate holder on all policies',
      'Self-insured without PCF requires $1M/$3M coverage limits',
    ],
  },

  {
    id: 'LA-A5',
    itemId: 'A5',
    stateCode: 'LA',
    title: 'Operating Hours and Availability',
    description: 'Louisiana requires posted business hours and 24/7 RN availability',
    severity: 'high',
    regulationCode: '48-I-9115',
    regulationName: 'Operations',
    agencyName: 'Louisiana Department of Health - Health Standards Section',
    agencyAbbr: 'LDH-HSS',
    agencyPhone: '(225) 342-0138',
    agencyUrl: 'https://ldh.la.gov/health-standards-section/home-health',
    questions: [
      {
        id: 'q1',
        question: 'Is your agency open at least 8 hours/day, 5 days/week between 7 AM and 6 PM?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you post hours of operation and emergency contact procedures prominently?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Is an RN immediately available by telecommunications 24 hours/day, 7 days/week?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'LAC 48:I.9115.B.1.c requires RN availability at all times',
      },
      {
        id: 'q4',
        question: 'Do you maintain at least 2 patients at all times?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required to maintain operational status per LAC 48:I.9115.B.1.f',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q4', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q4', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Agency operations meet LAC 48:I.9115 requirements.',
      atRisk: 'Operating hours, posted information, or patient census may not meet requirements. Review LAC 48:I.9115.',
      nonCompliant: '24/7 RN availability is required per LAC 48:I.9115.B.1.c. Implement on-call RN coverage immediately.',
      unknown: 'Review agency operations against LAC 48:I.9115 requirements.',
    },
    stateNotes: [
      'Must be open 8+ hours/day, 5 days/week between 7 AM-6 PM per LAC 48:I.9115',
      'RN must be available by telecommunications 24/7 per LAC 48:I.9115.B.1.c',
      'Must maintain minimum 2 patients at all times per LAC 48:I.9115.B.1.f',
      'Hours of operation and emergency procedures must be prominently posted',
    ],
  },

  // ============================================
  // SECTION B: BACKGROUND SCREENING
  // ============================================
  {
    id: 'LA-B1',
    itemId: 'B1',
    stateCode: 'LA',
    title: 'Criminal Background Check',
    description: 'Louisiana requires LSP criminal history check per R.S. 40:1203.1-5',
    severity: 'high',
    regulationCode: '40-1203.1-5',
    regulationName: 'Background Check',
    agencyName: 'Louisiana State Police - Bureau of Criminal Identification',
    agencyAbbr: 'LSP',
    agencyPhone: '(225) 925-6095',
    agencyUrl: 'https://ibc.dps.louisiana.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you obtain Louisiana State Police criminal history checks for ALL non-licensed staff BEFORE making an offer of employment?',
        inputType: 'select',
        options: [
          { value: 'always_before', label: 'Yes, always before any job offer' },
          { value: 'after_offer', label: 'After conditional offer but before start' },
          { value: 'after_start', label: 'After they start working' },
          { value: 'no', label: 'No / not consistently' },
        ],
        required: true,
        helpText: 'R.S. 40:1203.2(A)(1) requires check BEFORE any offer of employment',
      },
      {
        id: 'q2',
        question: 'How do you obtain criminal history checks?',
        inputType: 'select',
        options: [
          { value: 'ibc', label: 'LSP Internet Background Check (IBC) system' },
          { value: 'authorized', label: 'Through LSP Authorized Agency' },
          { value: 'fingerprints', label: 'Fingerprint submission to LSP' },
          { value: 'other', label: 'Other method / third-party only' },
        ],
        required: true,
        helpText: 'Must use LSP-approved methods per R.S. 40:1203.2',
      },
      {
        id: 'q3',
        question: 'Do you perform a security check (national sex offender registry) for all applicants?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per R.S. 40:1203.2(C)',
      },
      {
        id: 'q4',
        question: 'If using temporary employment pending background results, is staff under direct supervision of permanent employee or immediate family member?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, always supervised' },
          { value: 'no_temp', label: 'No temporary employment used' },
          { value: 'no_supervision', label: 'Temporary staff work without supervision' },
        ],
        required: true,
        helpText: 'Required per R.S. 40:1203.2(C)(3) if temporary employment is offered',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'after_start', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q4', value: 'no_supervision', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always_before' },
        { condition: 'includes', questionId: 'q2', value: ['ibc', 'authorized', 'fingerprints'] },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'includes', questionId: 'q4', value: ['yes', 'no_temp'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'other' },
      ], result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'after_offer' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Criminal background check procedures meet R.S. 40:1203.1-5 requirements.',
      atRisk: 'Background check timing or procedures may not fully comply. Louisiana requires LSP check BEFORE any job offer.',
      nonCompliant: 'Criminal background check procedures violate R.S. 40:1203.1-5. Employees working before background check clearance is a direct violation. Louisiana requires LSP-approved criminal history checks BEFORE offering employment. Contact LSP at (225) 925-6095.',
      unknown: 'Verify background check procedures meet R.S. 40:1203.1-5 requirements.',
    },
    stateNotes: [
      'R.S. 40:1203.2(A)(1) requires LSP check BEFORE any offer of employment',
      'Use LSP Internet Background Check (IBC) system at ibc.dps.louisiana.gov',
      'National sex offender registry check required per R.S. 40:1203.2(C)',
      'Temporary employment requires direct supervision per R.S. 40:1203.2(C)(3)',
      'LSP Bureau of Criminal Identification: (225) 925-6095',
    ],
  },

  {
    id: 'LA-B2',
    itemId: 'B2',
    stateCode: 'LA',
    title: 'DSW/CNA Registry Checks',
    description: 'Louisiana requires checking DSW and CNA registries at hire and every 6 months',
    severity: 'high',
    regulationCode: '48-I-9119',
    regulationName: 'Registry Verification',
    agencyName: 'Louisiana Department of Health',
    agencyAbbr: 'LDH',
    agencyPhone: '(225) 342-0138',
    agencyUrl: 'https://ldh.la.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the Direct Service Worker (DSW) Registry before hiring non-licensed personnel?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before hire' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'no', label: 'No / not aware of this requirement' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you check the Louisiana Certified Nurse Aide (CNA) Registry before hiring non-licensed personnel?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before hire' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Do you re-check the DSW/CNA registries for current employees at least every 6 months?',
        inputType: 'select',
        options: [
          { value: 'yes_6mo', label: 'Yes, every 6 months or more frequently' },
          { value: 'annual', label: 'Annually only' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'No ongoing checks' },
        ],
        required: true,
        helpText: 'LAC 48:I.9120.B requires checks at least every 6 months',
      },
      {
        id: 'q4',
        question: 'Do you document registry check results in personnel files?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'always' },
        { condition: 'equals', questionId: 'q3', value: 'yes_6mo' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'sometimes' },
        { condition: 'equals', questionId: 'q2', value: 'sometimes' },
        { condition: 'includes', questionId: 'q3', value: ['annual', 'hire_only', 'no'] },
        { condition: 'equals', questionId: 'q4', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'DSW/CNA registry check procedures meet LAC 48:I.9119-9120 requirements.',
      atRisk: 'Registry checks may not occur at required 6-month intervals or may not be properly documented. Review LAC 48:I.9120.B.',
      nonCompliant: 'Failure to check DSW/CNA registries before hiring violates LAC 48:I.9119.A.13. Employees with abuse/neglect findings cannot be employed.',
      unknown: 'Verify registry check procedures meet LAC 48:I.9119-9120 requirements.',
    },
    stateNotes: [
      'Check DSW and CNA registries before hiring per LAC 48:I.9119.A.13',
      'Re-check registries at least every 6 months per LAC 48:I.9120.B',
      'Employees with abuse/neglect findings cannot be employed',
      'Document registry check results in personnel files',
      'Louisiana-specific: more frequent than federal minimum requirements',
    ],
  },

  {
    id: 'LA-B3',
    itemId: 'B3',
    stateCode: 'LA',
    title: 'OIG/SAM Exclusion Verification',
    description: 'Federal requirement to verify staff not excluded from federal healthcare programs',
    severity: 'high',
    regulationCode: '42-1320a-7',
    regulationName: 'LEIE',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the OIG LEIE (List of Excluded Individuals/Entities) before hiring?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before hire' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you check SAM.gov for exclusions?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'How often do you re-check current employees against exclusion lists?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'quarterly', label: 'Quarterly' },
          { value: 'annual', label: 'Annually' },
          { value: 'hire_only', label: 'At hire only' },
        ],
        required: true,
        helpText: 'OIG recommends monthly checks',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'monthly' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'sometimes' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'includes', questionId: 'q3', value: ['annual', 'hire_only'] },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'OIG/SAM exclusion verification meets federal requirements.',
      atRisk: 'Exclusion checks may not be frequent enough. OIG recommends monthly screening of all employees.',
      nonCompliant: 'Failure to verify OIG exclusion status can result in significant federal penalties including CMPs.',
      unknown: 'Verify exclusion check procedures at exclusions.oig.hhs.gov.',
    },
    stateNotes: [
      'OIG LEIE check required at hire and monthly for Medicare/Medicaid providers',
      'SAM.gov exclusion check also required',
      'Excluded individuals cannot provide or bill for services',
      'Civil monetary penalties up to $100,000 per excluded person',
      'OIG Hotline: (800) 447-8477',
    ],
  },

  // ============================================
  // SECTION C: PERSONNEL FILES & TRAINING
  // ============================================
  {
    id: 'LA-C1',
    itemId: 'C1',
    stateCode: 'LA',
    title: 'Personnel File Requirements',
    description: 'Louisiana requires specific documents in each staff personnel file',
    severity: 'high',
    regulationCode: '48-I-9119.B',
    regulationName: 'Personnel',
    agencyName: 'Louisiana Department of Health - Health Standards Section',
    agencyAbbr: 'LDH-HSS',
    agencyPhone: '(225) 342-0138',
    agencyUrl: 'https://ldh.la.gov/health-standards-section/home-health',
    questions: [
      {
        id: 'q1',
        question: 'Which documents do you maintain in EVERY staff personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'name_address', label: 'Name, address, telephone number' },
          { value: 'app_resume', label: 'Job application/résumé' },
          { value: 'health', label: 'Annual employee health screening results' },
          { value: 'license', label: 'Current license/certification verification (if applicable)' },
          { value: 'job_desc', label: 'Current job description including duties' },
          { value: 'orientation', label: 'Documentation of orientation' },
          { value: 'contract', label: 'Current contract (if applicable)' },
          { value: 'eval', label: 'Annual personnel evaluations' },
          { value: 'ce', label: 'Documentation of continuing education' },
          { value: 'background', label: 'Criminal background investigation' },
          { value: 'registry', label: 'Registry checks (DSW/CNA)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all Louisiana-required documentation per LAC 48:I.9119.B.',
      atRisk: 'Some personnel files may be missing required documents. Review LAC 48:I.9119.B requirements.',
      nonCompliant: 'Personnel files are missing critical documentation required by LAC 48:I.9119.B. LDH surveyors will cite incomplete files.',
      unknown: 'Audit personnel files against LAC 48:I.9119.B requirements.',
    },
    stateNotes: [
      'LAC 48:I.9119.B requires 11 categories of documentation',
      'Annual employee health screening results required',
      'Criminal background investigation documentation required',
      'Registry checks (DSW/CNA) must be documented',
      'Annual personnel evaluations required',
    ],
  },

  {
    id: 'LA-C2',
    itemId: 'C2',
    stateCode: 'LA',
    title: 'Professional License Verification',
    description: 'All licensed personnel must have current, verified credentials',
    severity: 'high',
    regulationCode: '48-I-9119.A.6',
    regulationName: 'License Verification',
    agencyName: 'Louisiana State Board of Nursing',
    agencyAbbr: 'LSBN',
    agencyPhone: '(225) 755-7500',
    agencyUrl: 'https://www.lsbn.state.la.us/',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify all professional licenses (RN, LPN, PT, OT, etc.) before staff provide services?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always verified before first shift' },
          { value: 'sometimes', label: 'Sometimes / not consistently' },
          { value: 'no', label: 'No verification process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you verify licenses annually thereafter?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'LAC 48:I.9119.A.6 requires annual verification',
      },
      {
        id: 'q3',
        question: 'Do you track license expiration dates proactively?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, with tracking system' },
          { value: 'no', label: 'No / manual review only' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'sometimes' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      // Not proactively tracking license expiration dates
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Professional license verification meets LAC 48:I.9119.A.6 requirements.',
      atRisk: 'License verification may be inconsistent or expiration dates not tracked proactively. Louisiana requires verification at hire and annually.',
      nonCompliant: 'No license verification process. Staff with expired/invalid licenses violate Louisiana regulations.',
      unknown: 'Verify license verification procedures per LAC 48:I.9119.A.6.',
    },
    stateNotes: [
      'Verify all licenses (RN, LPN, PT, OT) before first shift per LAC 48:I.9119.A.6',
      'Annual re-verification required',
      'Louisiana State Board of Nursing: (225) 755-7500',
      'Track license expiration dates proactively',
      'Primary source verification recommended',
    ],
  },

  {
    id: 'LA-C3',
    itemId: 'C3',
    stateCode: 'LA',
    title: 'Home Health Aide Training and In-Service',
    description: 'Louisiana requires specific HHA training and 12 hours annual in-service',
    severity: 'high',
    regulationCode: '48-I-9103.C',
    regulationName: 'HHA Training',
    agencyName: 'Louisiana Department of Health - Health Standards Section',
    agencyAbbr: 'LDH-HSS',
    agencyPhone: '(225) 342-0138',
    agencyUrl: 'https://ldh.la.gov/health-standards-section/home-health',
    questions: [
      {
        id: 'q1',
        question: 'Do all Home Health Aides meet ONE of these Louisiana qualifications?',
        inputType: 'select',
        options: [
          { value: 'cna', label: 'Current nursing assistant certification + agency competency evaluation' },
          { value: 'hha_program', label: 'Completed HHA training program + agency competency evaluation' },
          { value: 'neither', label: 'Neither / not verified' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you provide at least 12 hours of in-service training per calendar year to each HHA?',
        inputType: 'select',
        options: [
          { value: 'yes_12', label: 'Yes, 12+ hours annually' },
          { value: 'partial', label: 'Some hours, but less than 12' },
          { value: 'no', label: 'No in-service program' },
        ],
        required: true,
        helpText: 'LAC 48:I.9103.C.8 requires minimum 12 hours per calendar year',
      },
      {
        id: 'q3',
        question: 'Does your in-service training include these required topics? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'body_care', label: 'Care of the body' },
          { value: 'communication', label: 'Communication' },
          { value: 'infection', label: 'Infection control' },
          { value: 'safety', label: 'Safety' },
          { value: 'documentation', label: 'Documentation' },
        ],
        helpText: 'Required topics per LAC 48:I.9103.C.8.a',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['cna', 'hha_program'] },
        { condition: 'equals', questionId: 'q2', value: 'yes_12' },
        { condition: 'countAtLeast', questionId: 'q3', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'neither', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q3', value: 4, result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'partial' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HHA qualifications and in-service training meet LAC 48:I.9103.C requirements.',
      atRisk: 'In-service training hours or topics may be insufficient. Louisiana requires 12 hours per calendar year.',
      nonCompliant: 'HHAs do not meet Louisiana qualification requirements per LAC 48:I.9103.C. All HHAs must have CNA certification or completed HHA training program.',
      unknown: 'Verify HHA qualifications and training per LAC 48:I.9103.C.',
    },
    stateNotes: [
      'HHAs must have CNA certification OR completed HHA training program',
      'Agency competency evaluation required per LAC 48:I.9103.C',
      '12 hours in-service training per calendar year per LAC 48:I.9103.C.8',
      'Required topics: body care, communication, infection control, safety, documentation',
      'Document all training in personnel files',
    ],
  },

  {
    id: 'LA-C4',
    itemId: 'C4',
    stateCode: 'LA',
    title: 'Administrator and Clinical Manager Continuing Education',
    description: 'Louisiana requires 2 hours annual CE for Administrator and Clinical Manager',
    severity: 'medium',
    regulationCode: '48-I-9103.A.3',
    regulationName: 'Continuing Education',
    agencyName: 'Louisiana Department of Health - Health Standards Section',
    agencyAbbr: 'LDH-HSS',
    agencyPhone: '(225) 342-0138',
    agencyUrl: 'https://ldh.la.gov/health-standards-section/home-health',
    questions: [
      {
        id: 'q1',
        question: 'Does your Administrator complete at least 2 hours of continuing education annually?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your Clinical Manager complete at least 2 hours of continuing education annually?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Does the CE include relevant topics? (Select all covered)',
        inputType: 'checklist',
        options: [
          { value: 'medicare_medicaid', label: 'Medicare and Medicaid regulations' },
          { value: 'management', label: 'Management practices' },
          { value: 'labor', label: 'Labor laws' },
          { value: 'osha', label: 'OSHA rules' },
          { value: 'ethics', label: 'Ethics' },
          { value: 'qi', label: 'Quality improvement' },
        ],
        helpText: 'Suggested topics per LAC 48:I.9103.A.3 and B.3',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      // CE topics coverage per LAC 48:I.9103
      { condition: 'countAtLeast', questionId: 'q3', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q3', value: 2, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Administrator and Clinical Manager CE requirements are met per LAC 48:I.9103.',
      atRisk: 'CE documentation may be incomplete or may not cover recommended topics. Verify 2 hours annually for both positions.',
      nonCompliant: 'Louisiana requires 2 hours annual CE for Administrator and Clinical Manager per LAC 48:I.9103.A.3 and B.3.',
      unknown: 'Review CE requirements per LAC 48:I.9103.',
    },
    stateNotes: [
      '2 hours annual CE required for Administrator per LAC 48:I.9103.A.3',
      '2 hours annual CE required for Clinical Manager per LAC 48:I.9103.B.3',
      'Suggested topics: Medicare/Medicaid, management, labor laws, OSHA, ethics, QI',
      'Document CE completion in personnel files',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'LA-D1',
    itemId: 'D1',
    stateCode: 'LA',
    title: 'Annual Employee Health Screening',
    description: 'Louisiana requires annual health screening for all staff',
    severity: 'high',
    regulationCode: '48-I-9119.A.4',
    regulationName: 'Health Screening',
    agencyName: 'Louisiana Department of Health',
    agencyAbbr: 'LDH',
    agencyPhone: '(225) 342-0138',
    agencyUrl: 'https://ldh.la.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL patient care staff have current annual health screening on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have current screening' },
          { value: 'most', label: 'Most do, some may need updating' },
          { value: 'no', label: 'No / not tracking' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your health screening include TB screening?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Annual employee health screening meets LAC 48:I.9119.A.4 requirements.',
      atRisk: 'Some health screenings may be overdue. Ensure annual screenings including TB for all staff.',
      nonCompliant: 'Employee health screenings are not being tracked per LAC 48:I.9119.A.4.',
      unknown: 'Review health screening procedures per LAC 48:I.9119.A.4.',
    },
    stateNotes: [
      'Annual health screening required for all patient care staff per LAC 48:I.9119.A.4',
      'TB screening must be included',
      'Document screening results in personnel files',
      'Track due dates to ensure annual compliance',
    ],
  },

  {
    id: 'LA-D2',
    itemId: 'D2',
    stateCode: 'LA',
    title: 'Emergency Preparedness Plan',
    description: 'Louisiana requires emergency plan conforming to Louisiana Model HH Emergency Preparedness Plan',
    severity: 'high',
    regulationCode: '48-I-9121',
    regulationName: 'Emergency Preparedness',
    agencyName: 'Louisiana Governor\'s Office of Homeland Security and Emergency Preparedness',
    agencyAbbr: 'GOHSEP',
    agencyPhone: '(225) 925-7500',
    agencyUrl: 'https://gohsep.la.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written emergency preparedness plan that conforms to the Louisiana Model Home Health Emergency Preparedness Plan?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
        helpText: 'LAC 48:I.9121.A requires plan conforming to Louisiana OEP model plan',
      },
      {
        id: 'q2',
        question: 'Does your plan include all required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'evacuation', label: 'Evacuation procedures for patients needing community assistance' },
          { value: 'essential_care', label: 'Delivery of essential care to patients in shelters or other locations' },
          { value: 'staff_mgmt', label: 'Staff management including responsibility distribution' },
          { value: 'transport', label: 'Coordination of transportation for evacuating patients' },
          { value: 'family_notify', label: 'Notification of patient family/caregiver if evacuated' },
        ],
        helpText: 'Required elements per LAC 48:I.9121.B',
      },
      {
        id: 'q3',
        question: 'Have you activated your emergency plan at least annually (actual emergency or drill)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required by LAC 48:I.9121.C',
      },
      {
        id: 'q4',
        question: 'Have you submitted plan updates to your parish Office of Emergency Preparedness?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / not sure' },
        ],
        helpText: 'Required by LAC 48:I.9121.D',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q4', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'countLessThan', questionId: 'q2', value: 5 },
        { condition: 'equals', questionId: 'q3', value: 'no' },
        { condition: 'equals', questionId: 'q4', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness plan meets LAC 48:I.9121 requirements.',
      atRisk: 'Emergency plan may be incomplete, untested, or not submitted to parish OEP. Louisiana requires annual testing and parish submission.',
      nonCompliant: 'No Louisiana Model Emergency Preparedness Plan on file. This is critical for hurricane-prone state. Contact GOHSEP for model plan.',
      unknown: 'Review emergency preparedness against LAC 48:I.9121 requirements.',
    },
    stateNotes: [
      'Must conform to Louisiana Model Home Health Emergency Preparedness Plan per LAC 48:I.9121.A',
      'Plan must include evacuation, essential care, staff management, transport, family notification',
      'Annual testing or drill required per LAC 48:I.9121.C',
      'Submit updates to parish Office of Emergency Preparedness per LAC 48:I.9121.D',
      'GOHSEP: (225) 925-7500 for model plan',
    ],
  },

  // ============================================
  // SECTION E: PATIENT CARE
  // ============================================
  {
    id: 'LA-E1',
    itemId: 'E1',
    stateCode: 'LA',
    title: 'Plan of Care Requirements',
    description: 'Louisiana requires individualized plan of care signed by physician',
    severity: 'high',
    regulationCode: '48-I-9123.C',
    regulationName: 'Plan of Care',
    agencyName: 'Louisiana Department of Health - Health Standards Section',
    agencyAbbr: 'LDH-HSS',
    agencyPhone: '(225) 342-0138',
    agencyUrl: 'https://ldh.la.gov/health-standards-section/home-health',
    questions: [
      {
        id: 'q1',
        question: 'Do you complete an initial plan of care within 7 days from start of care?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always within 7 days' },
          { value: 'usually', label: 'Usually, but sometimes longer' },
          { value: 'no', label: 'No formal timeline' },
        ],
        required: true,
        helpText: 'LAC 48:I.9123.B.2 requires initial POC within 7 days',
      },
      {
        id: 'q2',
        question: 'Are plans of care signed by the authorized healthcare provider within 60 days?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always within 60 days' },
          { value: 'usually', label: 'Usually' },
          { value: 'no', label: 'No / not tracked' },
        ],
        required: true,
        helpText: 'LAC 48:I.9123.C.1 - physician signature required within 60 days',
      },
      {
        id: 'q3',
        question: 'Is the total plan of care reviewed with the physician at least every 60 days?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required by LAC 48:I.9123.D',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'always' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'equals', questionId: 'q2', value: 'usually' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Plan of care procedures meet LAC 48:I.9123 requirements.',
      atRisk: 'POC completion or physician signature timing may be inconsistent. Louisiana requires POC within 7 days and physician signature within 60 days.',
      nonCompliant: 'Plan of care procedures do not meet LAC 48:I.9123 requirements. POC must be completed within 7 days and reviewed every 60 days.',
      unknown: 'Review POC procedures against LAC 48:I.9123 requirements.',
    },
    stateNotes: [
      'Initial POC required within 7 days from start of care per LAC 48:I.9123.B.2',
      'Physician signature required within 60 days per LAC 48:I.9123.C.1',
      'Total POC review with physician every 60 days per LAC 48:I.9123.D',
      'POC must be individualized to patient needs',
    ],
  },

  {
    id: 'LA-E2',
    itemId: 'E2',
    stateCode: 'LA',
    title: 'Home Health Aide Supervision',
    description: 'Louisiana requires specific supervision intervals for HHAs',
    severity: 'high',
    regulationCode: '48-I-9103.C.7',
    regulationName: 'HHA Supervision',
    agencyName: 'Louisiana Department of Health - Health Standards Section',
    agencyAbbr: 'LDH-HSS',
    agencyPhone: '(225) 342-0138',
    agencyUrl: 'https://ldh.la.gov/health-standards-section/home-health',
    questions: [
      {
        id: 'q1',
        question: 'For patients receiving skilled services (nursing, PT, OT, SLP), how often do you conduct supervisory visits with the HHA present?',
        inputType: 'select',
        options: [
          { value: 'every_14', label: 'At least every 14 days' },
          { value: 'every_30', label: 'Every 30 days' },
          { value: 'every_60', label: 'Every 60 days' },
          { value: 'no_schedule', label: 'No regular schedule' },
        ],
        required: true,
        helpText: 'LAC 48:I.9103.C.7.c requires supervision at least every 14 days for skilled patients',
      },
      {
        id: 'q2',
        question: 'For patients NOT receiving skilled services, how often does an RN conduct supervisory visits with the HHA present?',
        inputType: 'select',
        options: [
          { value: 'every_60', label: 'At least every 60 days' },
          { value: 'every_90', label: 'Every 90 days' },
          { value: 'no_schedule', label: 'No regular schedule' },
        ],
        required: true,
        helpText: 'LAC 48:I.9103.C.7.d requires RN supervisory visit at least every 60 days for non-skilled patients',
      },
      {
        id: 'q3',
        question: 'Do you document supervision including aide-patient relationships, services provided, and instructions given?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required by LAC 48:I.9103.C.7.e',
      },
      {
        id: 'q4',
        question: 'Do you conduct annual performance reviews for each HHA documented in personnel files?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required by LAC 48:I.9103.C.7.f',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'every_14' },
        { condition: 'equals', questionId: 'q2', value: 'every_60' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q4', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no_schedule' },
        { condition: 'equals', questionId: 'q2', value: 'no_schedule' },
      ], result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['every_30', 'every_60'] },
        { condition: 'equals', questionId: 'q2', value: 'every_90' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
        { condition: 'equals', questionId: 'q4', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'HHA supervision meets LAC 48:I.9103.C.7 requirements.',
      atRisk: 'Supervision intervals may not meet Louisiana requirements. Skilled patients require supervision every 14 days; non-skilled every 60 days.',
      nonCompliant: 'No regular HHA supervision schedule. This violates LAC 48:I.9103.C.7.',
      unknown: 'Review HHA supervision procedures against LAC 48:I.9103.C.7.',
    },
    stateNotes: [
      'Skilled patients: supervisory visit every 14 days per LAC 48:I.9103.C.7.c',
      'Non-skilled patients: RN supervisory visit every 60 days per LAC 48:I.9103.C.7.d',
      'Document: aide-patient relationships, services, instructions per LAC 48:I.9103.C.7.e',
      'Annual performance reviews required per LAC 48:I.9103.C.7.f',
    ],
  },

  {
    id: 'LA-E3',
    itemId: 'E3',
    stateCode: 'LA',
    title: 'Patient Rights',
    description: 'Louisiana requires written patient rights notice and documentation',
    severity: 'high',
    regulationCode: '48-I-9125',
    regulationName: 'Patient Rights',
    agencyName: 'Louisiana Department of Health - Health Standards Section',
    agencyAbbr: 'LDH-HSS',
    agencyPhone: '(225) 342-0138',
    agencyUrl: 'https://ldh.la.gov/health-standards-section/home-health',
    questions: [
      {
        id: 'q1',
        question: 'Do you inform patients of their rights in a language and manner they understand?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you advise patients of the state toll-free home health hotline?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required by LAC 48:I.9125.A.9',
      },
      {
        id: 'q3',
        question: 'Do you provide advance written notice before reducing or terminating ongoing care?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required by LAC 48:I.9125.A.8',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient rights procedures meet LAC 48:I.9125 requirements.',
      atRisk: 'Some patient rights procedures may be incomplete.',
      nonCompliant: 'Patient rights requirements are not being met per LAC 48:I.9125.',
      unknown: 'Review patient rights procedures against LAC 48:I.9125.',
    },
    stateNotes: [
      'Inform patients of rights in language they understand per LAC 48:I.9125',
      'Must provide state toll-free home health hotline per LAC 48:I.9125.A.9',
      'Written advance notice required before reducing/terminating care per LAC 48:I.9125.A.8',
      'LDH-HSS Complaint Line: (800) 256-2748',
    ],
  },

  // ============================================
  // SECTION F: DOCUMENTATION & RECORDS
  // ============================================
  {
    id: 'LA-F1',
    itemId: 'F1',
    stateCode: 'LA',
    title: 'Clinical Record Requirements',
    description: 'Louisiana requires specific clinical record documentation',
    severity: 'high',
    regulationCode: '48-I-9129',
    regulationName: 'Clinical Records',
    agencyName: 'Louisiana Department of Health - Health Standards Section',
    agencyAbbr: 'LDH-HSS',
    agencyPhone: '(225) 342-0138',
    agencyUrl: 'https://ldh.la.gov/health-standards-section/home-health',
    questions: [
      {
        id: 'q1',
        question: 'Which elements are included in your clinical notes for each visit? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'date', label: 'Date of visit' },
          { value: 'arrival', label: 'Time of arrival' },
          { value: 'exit', label: 'Time of exit' },
          { value: 'services', label: 'Services rendered and/or justification' },
          { value: 'signature', label: 'Signature of person making visit' },
          { value: 'vitals', label: 'Vital signs per order or standard of practice' },
          { value: 'comments', label: 'Comments when indicated' },
        ],
        required: true,
        helpText: 'Required elements per LAC 48:I.9129.B',
      },
      {
        id: 'q2',
        question: 'Are clinical notes incorporated into the record within one week of the visit?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always within one week' },
          { value: 'usually', label: 'Usually' },
          { value: 'no', label: 'No / often delayed' },
        ],
        required: true,
        helpText: 'Required by LAC 48:I.9129.B',
      },
      {
        id: 'q3',
        question: 'Does the patient or responsible person sign the permanent record of visit?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per LAC 48:I.9129.B note',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 6 },
        { condition: 'equals', questionId: 'q2', value: 'always' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'usually' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Clinical documentation meets LAC 48:I.9129 requirements.',
      atRisk: 'Clinical documentation may be incomplete or delayed. Review LAC 48:I.9129.B requirements.',
      nonCompliant: 'Clinical documentation does not meet LAC 48:I.9129 requirements.',
      unknown: 'Review clinical documentation against LAC 48:I.9129.',
    },
    stateNotes: [
      'Required elements: date, arrival time, exit time, services, signature, vitals per LAC 48:I.9129.B',
      'Notes must be incorporated within one week of visit',
      'Patient or responsible person must sign permanent record of visit',
      'Comments required when indicated',
    ],
  },

  {
    id: 'LA-F2',
    itemId: 'F2',
    stateCode: 'LA',
    title: 'Record Retention',
    description: 'Louisiana requires 6 years record retention',
    severity: 'high',
    regulationCode: '48-I-9129.A.8',
    regulationName: 'Record Retention',
    agencyName: 'Louisiana Department of Health - Health Standards Section',
    agencyAbbr: 'LDH-HSS',
    agencyPhone: '(225) 342-0138',
    agencyUrl: 'https://ldh.la.gov/health-standards-section/home-health',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain clinical records?',
        inputType: 'select',
        options: [
          { value: '6plus', label: '6 years or longer' },
          { value: '5', label: '5 years' },
          { value: 'less5', label: 'Less than 5 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Louisiana LAC 48:I.9129.A.8 requires minimum 6 years retention',
      },
      {
        id: 'q2',
        question: 'Do you have internal policies for record retention even if the agency discontinues operation?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required by LAC 48:I.9129.A.9',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: '6plus' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: '5', result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['less5', 'unsure'] },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention meets Louisiana\'s 6-year requirement per LAC 48:I.9129.A.8.',
      atRisk: 'Louisiana requires 6-year record retention (not 5 years). Update your retention policy.',
      nonCompliant: 'Record retention does not meet Louisiana\'s 6-year requirement under LAC 48:I.9129.A.8.',
      unknown: 'Review record retention requirements under LAC 48:I.9129.A.8.',
    },
    stateNotes: [
      'Louisiana requires 6-year record retention per LAC 48:I.9129.A.8',
      'This exceeds federal 5-year minimum',
      'Policies required for records even if agency discontinues operation per LAC 48:I.9129.A.9',
      'Records must be safeguarded from loss or unauthorized use',
    ],
  },

  // ============================================
  // SECTION G: QUALITY & REPORTING
  // ============================================
  {
    id: 'LA-G1',
    itemId: 'G1',
    stateCode: 'LA',
    title: 'Quality Assessment and Performance Improvement (QAPI)',
    description: 'Louisiana requires quarterly QAPI activities and documentation',
    severity: 'high',
    regulationCode: '48-I-9131',
    regulationName: 'QAPI',
    agencyName: 'Louisiana Department of Health - Health Standards Section',
    agencyAbbr: 'LDH-HSS',
    agencyPhone: '(225) 342-0138',
    agencyUrl: 'https://ldh.la.gov/health-standards-section/home-health',
    questions: [
      {
        id: 'q1',
        question: 'Do you have an ongoing, agency-wide, data-driven QAPI program?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you conduct QAPI activities at least quarterly?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'LAC 48:I.9131.B requires quarterly QAPI evaluation',
      },
      {
        id: 'q3',
        question: 'Does your QAPI include quarterly clinical record review?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q4',
        question: 'Are QAPI results reported to the governing body?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required by LAC 48:I.9131.B',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q4', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
        { condition: 'equals', questionId: 'q4', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'QAPI program meets LAC 48:I.9131 requirements.',
      atRisk: 'QAPI activities may not be quarterly or not reported to governing body.',
      nonCompliant: 'No QAPI program in place. This violates LAC 48:I.9131.',
      unknown: 'Review QAPI program against LAC 48:I.9131 requirements.',
    },
    stateNotes: [
      'Agency-wide, data-driven QAPI program required per LAC 48:I.9131',
      'Quarterly QAPI activities and evaluation required per LAC 48:I.9131.B',
      'Quarterly clinical record review required',
      'Results must be reported to governing body',
    ],
  },

  {
    id: 'LA-G2',
    itemId: 'G2',
    stateCode: 'LA',
    title: 'Abuse/Neglect Reporting',
    description: 'Louisiana requires mandatory reporting to APS (18-59) and EPS (60+)',
    severity: 'high',
    regulationCode: '14-403.2',
    regulationName: 'Abuse Reporting',
    agencyName: 'Louisiana Adult Protective Services / Elderly Protective Services',
    agencyAbbr: 'APS/EPS',
    agencyPhone: '(800) 898-4910',
    agencyUrl: 'https://ldh.la.gov/office-of-aging-and-adult-services/adult-protective-services',
    questions: [
      {
        id: 'q1',
        question: 'Do all staff receive training on recognizing and reporting abuse/neglect?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff trained' },
          { value: 'most', label: 'Most staff trained' },
          { value: 'no', label: 'No / not consistently' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do staff know the correct reporting numbers? (Select which staff know)',
        inputType: 'checklist',
        options: [
          { value: 'aps', label: 'Adults 18-59: Adult Protective Services (800) 898-4910' },
          { value: 'eps', label: 'Adults 60+: Elderly Protective Services (833) 577-6532 or (800) 898-4910' },
          { value: 'dcfs', label: 'Children: DCFS (855) 452-5437' },
        ],
        helpText: 'Louisiana has separate reporting for different age groups',
      },
      {
        id: 'q3',
        question: 'Do you have written abuse/neglect reporting procedures?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 2 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'countLessThan', questionId: 'q2', value: 2 },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Abuse/neglect reporting procedures meet La. R.S. 14:403.2 requirements.',
      atRisk: 'Some staff may not know correct reporting numbers. Louisiana requires different numbers for APS (18-59) vs EPS (60+).',
      nonCompliant: 'Staff are not trained on mandatory abuse reporting. This violates La. R.S. 14:403.2. Criminal penalties apply for failure to report.',
      unknown: 'Verify abuse reporting training per La. R.S. 14:403.2.',
    },
    stateNotes: [
      'Adults 18-59: Report to Adult Protective Services at (800) 898-4910',
      'Adults 60+: Report to Elderly Protective Services at (833) 577-6532 or (800) 898-4910',
      'Children: Report to DCFS at (855) 452-5437',
      'ALL Louisiana citizens are mandatory reporters under R.S. 14:403.2',
      'Good faith reporters have civil and criminal immunity',
    ],
  },

  {
    id: 'LA-G3',
    itemId: 'G3',
    stateCode: 'LA',
    title: 'Governing Body Requirements',
    description: 'Louisiana requires specific governing body activities',
    severity: 'medium',
    regulationCode: '48-I-9102',
    regulationName: 'Governing Body',
    agencyName: 'Louisiana Department of Health - Health Standards Section',
    agencyAbbr: 'LDH-HSS',
    agencyPhone: '(225) 342-0138',
    agencyUrl: 'https://ldh.la.gov/health-standards-section/home-health',
    questions: [
      {
        id: 'q1',
        question: 'Does your governing body conduct annual documented review of: (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'policies', label: 'Policies and procedures' },
          { value: 'budget', label: 'Budget' },
          { value: 'program_eval', label: 'Overall program evaluation' },
          { value: 'stats', label: 'Statistical information' },
          { value: 'complaints', label: 'Complaint resolutions' },
          { value: 'changes', label: 'Projected changes' },
          { value: 'emergency', label: 'Emergency preparedness' },
        ],
        required: true,
        helpText: 'Required by LAC 48:I.9102.B.1',
      },
      {
        id: 'q2',
        question: 'Do you maintain written meeting minutes with signatures, dates, and times?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Does the governing body receive and acknowledge QAPI evaluation results?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required by LAC 48:I.9102.B.4',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 6 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Governing body activities meet LAC 48:I.9102 requirements.',
      atRisk: 'Governing body documentation or activities may be incomplete.',
      nonCompliant: 'Governing body does not meet LAC 48:I.9102 requirements.',
      unknown: 'Review governing body activities against LAC 48:I.9102.',
    },
    stateNotes: [
      'Annual review required: policies, budget, program evaluation, statistics per LAC 48:I.9102.B.1',
      'Written meeting minutes with signatures, dates, times required',
      'QAPI results must be received and acknowledged per LAC 48:I.9102.B.4',
      'Must review complaints and projected changes',
    ],
  },
];

export default LOUISIANA_QUESTIONS;